

class B2BCart extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cart-form]');
    if (!this.form) return;
    this.addEventListener('click', this.#handleClick);
    this.baseline = this.#snapshot();
    this.savedValues = [...this.form.elements].filter(el => el.name && !['submit', 'button'].includes(el.type)).map(el => [el, el.value]);
    this.addEventListener('input', this.#handleChange);
    this.addEventListener('change', this.#handleChange);
    this.form.addEventListener('submit', this.#handleSubmit);
    try {
      const stored = JSON.parse(sessionStorage.getItem('b2b-cart-result') || 'null');
      sessionStorage.removeItem('b2b-cart-result');
      if (stored && Date.now() - stored.time < 60000 && stored.path === location.pathname) {
        for (const [name, value] of stored.draft || []) {
          const field = [...this.form.elements].find(el => el.name === name);
          if (field) field.value = value;
        }
        if (stored.success) {
          const notice = this.querySelector('[data-cart-success]');
          if (notice) { notice.hidden = false; this.successTimer = setTimeout(() => { notice.hidden = true; }, 3000); }
        }
      }
    } catch {}
    this.#handleChange();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('input', this.#handleChange);
    this.removeEventListener('change', this.#handleChange);
    this.form?.removeEventListener('submit', this.#handleSubmit);
    clearTimeout(this.successTimer);
  }

  #snapshot() {
    return JSON.stringify([...new FormData(this.form).entries()]);
  }

  #handleChange = () => {
    this.#validate();
    const dirty = this.#snapshot() !== this.baseline || !!this.querySelector('b2b-freight[data-mode="cart"]')?.countryDirty;
    this.dataset.dirty = String(dirty);
    this.querySelectorAll('[name="checkout"]').forEach(node => { node.hidden = dirty; node.disabled = dirty || this.busy; });
    this.querySelectorAll('[name="update"], [data-discard]').forEach(node => { node.hidden = !dirty; });
    this.querySelector('[data-edit-bar]')?.toggleAttribute('hidden', !dirty);
    this.querySelector('[data-dirty-notice]')?.toggleAttribute('hidden', !dirty);
    this.querySelector('[data-mobile-update]')?.toggleAttribute('hidden', !dirty);
    this.querySelector('[data-mobile-dirty-notice]')?.toggleAttribute('hidden', !dirty);
  };

  #handleClick = (event) => {
    if (event.target.closest('[data-dialog-close]')) { this.querySelector('dialog[open]')?.close(); return; }
    if (this.busy) return;
    if (event.target.closest('[data-confirm-remove]')) {
      this.querySelector('[data-remove-dialog]')?.close();
      if (this.removeGroup) this.#save(false, this.removeGroup);
      return;
    }
    if (event.target.closest('[data-discard]')) {
      this.querySelector('[data-discard-dialog]')?.showModal();
      return;
    }
    if (event.target.closest('[data-confirm-discard]')) {
      this.querySelector('[data-discard-dialog]')?.close();
      for (const [field, value] of this.savedValues) field.value = value;
      this.querySelector('b2b-freight[data-mode="cart"]')?.discardCountry();
      this.#handleChange();
      return;
    }
    const browse = event.target.closest('[data-save-before-navigation]');
    if (browse && this.dataset.dirty === 'true') {
      event.preventDefault();
      const notice = this.querySelector('[data-cart-error]');
      if (notice) {
        notice.textContent = this.dataset.browsePending || '';
        notice.hidden = false;
        notice.scrollIntoView({ block: 'center' });
      }
      this.querySelector(matchMedia('(max-width:749px)').matches ? '[data-mobile-update]' : '[data-edit-bar] button')?.focus();
      return;
    }
    const stepButton = event.target.closest('[data-step]');
    if (stepButton) {
      const input = stepButton.parentElement?.querySelector('[data-line-quantity]');
      if (!(input instanceof HTMLInputElement)) return;
      input.value = String(Math.max(0, Number(input.value || 0) + Number(stepButton.dataset.step || 0)));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    const removeButton = event.target.closest('[data-remove-group]');
    if (!removeButton) return;
    const group = removeButton.closest('[data-product-group]');
    this.removeGroup = group;
    const dialog = this.querySelector('[data-remove-dialog]');
    if (dialog) {
      dialog.querySelector('[data-remove-title]').textContent = group.querySelector('.b2b-cart__product-link strong')?.textContent || '';
      dialog.showModal();
    }
  };

  #validate = () => {
    let firstError = '';
    for (const group of this.querySelectorAll('[data-product-group]')) {
      const minimum = Math.max(1, Number(group.dataset.moq || 1));
      const total = [...group.querySelectorAll('[data-line-quantity]')]
        .reduce((sum, input) => sum + Math.max(0, Number(input.value || 0)), 0);
      const invalid = total > 0 && total < minimum;
      group.dataset.invalid = String(invalid);
      const totalNode = group.querySelector('[data-group-quantity]');
      if (totalNode) totalNode.textContent = String(total);
      const error = group.querySelector('[data-group-error]');
      if (error) {
        error.hidden = !invalid;
        error.textContent = invalid ? String(group.dataset.errorTemplate || '').replace('__COUNT__', String(minimum - total)) : '';
      }
      if (invalid && !firstError) firstError = error?.textContent || '';
    }
    this.#updatePricingGroups();
    const checkout = this.form?.querySelector('button[name="checkout"]');
    if (checkout) checkout.disabled = false;
    const formError = this.querySelector('[data-cart-error]');
    if (formError) {
      formError.hidden = !firstError;
      formError.textContent = firstError;
    }
    return !firstError;
  };

  #updatePricingGroups() {
    const quantities = new Map();
    const weights = new Map();
    for (const productGroup of this.querySelectorAll('[data-product-group]')) {
      const pricingGroup = productGroup.dataset.pricingGroup;
      if (!pricingGroup) continue;
      const quantity = [...productGroup.querySelectorAll('[data-line-quantity]')]
        .reduce((sum, input) => sum + Math.max(0, Number(input.value || 0)), 0);
      quantities.set(pricingGroup, (quantities.get(pricingGroup) || 0) + quantity);
      const inputs = [...productGroup.querySelectorAll('[data-line-quantity]')];
      const productWeight = productGroup.querySelector('[data-product-weight]');
      if (productWeight) this.#setWeight(productWeight, inputs);
      weights.set(pricingGroup, [...(weights.get(pricingGroup) || []), ...inputs]);
    }
    const orderWeight = this.querySelector('[data-order-weight]');
    if (orderWeight) this.#setWeight(orderWeight, [...this.querySelectorAll('[data-line-quantity]')]);

    for (const summary of this.querySelectorAll('[data-pricing-group-summary]')) {
      const quantity = quantities.get(summary.dataset.pricingGroup) || 0;
      const quantityNode = summary.querySelector('[data-pricing-quantity]');
      if (quantityNode) quantityNode.textContent = String(quantity);
      const groupWeight = summary.querySelector('[data-group-weight]');
      if (groupWeight) this.#setWeight(groupWeight, weights.get(summary.dataset.pricingGroup) || []);
      let tiers = [];
      try {
        tiers = JSON.parse(summary.dataset.tiers || '[]')
          .filter((tier) => tier.enabled !== false && Number(tier.quantity) > 0 && Number(tier.discount) > 0)
          .sort((a, b) => Number(a.quantity) - Number(b.quantity));
      } catch (error) {
        tiers = [];
      }
      const active = [...tiers].reverse().find((tier) => quantity >= Number(tier.quantity));
      const next = tiers.find((tier) => quantity < Number(tier.quantity));
      const rules = summary.querySelector('[data-tier-rules]');
      if (rules) {
        rules.replaceChildren(...tiers.map(tier => {
          const node = document.createElement('span');
          node.textContent = String(summary.dataset.tierLabel || '__COUNT__+ / −__DISCOUNT__%')
            .replace('__COUNT__', String(tier.quantity)).replace('__DISCOUNT__', String(tier.discount));
          node.dataset.active = String(tier === active);
          return node;
        }));
      }
      const progress = summary.querySelector('[data-tier-progress-label]');
      if (!progress) continue;
      const bar = summary.querySelector('[data-tier-progress]');
      const target = Number(tiers.at(-1)?.quantity || 1);
      if (bar) {
        bar.hidden = !tiers.length;
        bar.style.setProperty('--tier-progress', `${Math.min(100, quantity / target * 100)}%`);
        bar.setAttribute('aria-valuemax', String(target));
        bar.setAttribute('aria-valuenow', String(Math.min(quantity, target)));
      }
      if (next) {
        progress.textContent = String(summary.dataset.nextTemplate || '')
          .replace('__COUNT__', String(Number(next.quantity) - quantity))
          .replace('__DISCOUNT__', String(next.discount));
      } else if (active) {
        progress.textContent = String(summary.dataset.currentTemplate || '')
          .replace('__DISCOUNT__', String(active.discount));
      } else {
        progress.textContent = summary.dataset.noDiscount || '';
      }
      if (bar) bar.setAttribute('aria-valuetext', progress.textContent);
    }
  }

  #formatWeight(inputs) {
    let grams = 0;
    let missing = 0;
    for (const input of inputs) {
      const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
      const unit = Math.max(0, Number(input.dataset.unitGrams) || 0);
      grams += unit * quantity;
      if (!unit) missing += quantity;
    }
    if (!grams) return '';
    const weight = `${new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 3 }).format(grams / 1000)} kg`;
    return weight;
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.busy) return;
    const checkout = event.submitter?.name === 'checkout';
    if (checkout && this.#snapshot() !== this.baseline) { this.#handleChange(); return; }
    const valid = this.#validate();
    if (checkout && !valid) {
      this.querySelector('[data-invalid="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    await this.#save(checkout);
  };

  #setWeight(node, inputs) {
    const value = this.#formatWeight(inputs);
    node.textContent = value;
    (node.closest('[data-weight-field]') || node).hidden = !value;
  }

  async #save(checkout, removeGroup = null) {
    if (this.busy) return;
    this.busy = true;
    const buttons = [...this.querySelectorAll('button, input, textarea')];
    const disabledStates = buttons.map(button => button.disabled);
    const draft = removeGroup ? this.savedValues.filter(([field, value]) => field.value !== value && !removeGroup.contains(field)).map(([field]) => [field.name, field.value]) : [];
    buttons.forEach(button => { button.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    try {
      const updates = {};
      for (const input of (removeGroup || this.form).querySelectorAll('[data-line-quantity]')) {
        const match = input.name.match(/^updates\[(.+)\]$/);
        if (match) updates[match[1]] = removeGroup ? 0 : Math.max(0, Math.floor(Number(input.value) || 0));
      }
      const payload = { updates };
      const note = this.form.querySelector('[name="note"]');
      if (note && !removeGroup) payload.note = note.value;
      const attributes = {};
      for (const input of this.form.querySelectorAll('input[name]')) {
        const match = input.name.match(/^attributes\[(.+)\]$/);
        if (match) attributes[match[1]] = input.value;
      }
      if (Object.keys(attributes).length && !removeGroup) payload.attributes = attributes;
      const discount = this.form.querySelector('[name="discount"]')?.value.trim();
      if (discount && !removeGroup) payload.discount = discount;
      const url = new URL(this.form.dataset.cartUpdateUrl || (window.Shopify?.routes?.root || '/') + 'cart/update', location.origin);
      if (!url.pathname.endsWith('.js')) url.pathname += '.js';
      const response = await fetch(url.href, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload), credentials: 'same-origin',
      });
      const cart = await response.json();
      if (!response.ok || cart.status >= 400) throw new Error(cart.description || cart.message || this.dataset.error);
      if (!Array.isArray(cart.items)) throw new Error(this.dataset.error);
      for (const [key, quantity] of Object.entries(updates)) {
        const saved = cart.items.find(item => item.key === key);
        if (saved && saved.quantity !== quantity) throw new Error(this.dataset.saveMismatch || this.dataset.error);
      }
      // Discounts can change Shopify line keys. Also check product totals against
      // the submitted quantities so a changed key is not mistaken for a failure.
      for (const group of (removeGroup ? [removeGroup] : this.querySelectorAll('[data-product-group]'))) {
        const expected = removeGroup ? 0 : [...group.querySelectorAll('[data-line-quantity]')]
          .reduce((sum, input) => sum + Math.max(0, Math.floor(Number(input.value) || 0)), 0);
        const actual = cart.items.filter(item => String(item.product_id) === group.dataset.productId)
          .reduce((sum, item) => sum + item.quantity, 0);
        if (actual !== expected) throw new Error(this.dataset.saveMismatch || this.dataset.error);
      }
      if (checkout) {
        // Validate the saved server quantities, not just the input fields.
        for (const group of this.querySelectorAll('[data-product-group]')) {
          const quantity = (cart.items || []).filter(item => String(item.product_id) === group.dataset.productId)
            .reduce((sum, item) => sum + item.quantity, 0);
          if (quantity > 0 && quantity < Number(group.dataset.moq || 1)) {
            throw new Error(group.dataset.errorTemplate.replace('__COUNT__', String(Number(group.dataset.moq) - quantity)));
          }
        }
        if (!cart.items?.length) { location.assign(this.form.action); return; }
        location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
      } else {
        try { sessionStorage.setItem('b2b-cart-result', JSON.stringify({time: Date.now(), path: new URL(this.form.action).pathname, success: !removeGroup, draft})); } catch {}
        location.assign(this.form.action);
      }
    } catch (error) {
      const formError = this.querySelector('[data-cart-error]');
      if (formError) {
        formError.hidden = false;
        formError.textContent = error.message || this.dataset.error;
      }
      const dialog = this.querySelector('[data-error-dialog]');
      if (dialog) { dialog.querySelector('[data-error-message]').textContent = error.message || this.dataset.error; dialog.showModal(); }
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      buttons.forEach((button, index) => { button.disabled = disabledStates[index]; });
    }
  }
}

if (!customElements.get('b2b-cart')) customElements.define('b2b-cart', B2BCart);
